import { Loader } from '@getro/rombo';
import { ArrowUpDown, Columns, LayoutList, Table, TableCellsSplit, Funnel, Download } from 'lucide-react';
import PropTypes from 'prop-types';
import React from 'react';
import { Flex } from 'rebass/styled-components';
import { TableFields } from 'components/molecules/agGrid/components/tableFields';
import { LayoutToolbar } from './layoutToolbar';
import { ToolbarButton } from './toolbarButton';
import { SortToolbar } from './sortToolbar';

export const Toolbar = ({
  view,
  toolbarOptionChange,
  selectedColumns,
  otherToolbarOptions,
  isDownloadingCsv,
  type,
  page,
  filtersVisible,
  filtersCount,
  tableRef,
  currentSort,
  sortOptions,
  showLayout,
}) => {
  const options = [
    {
      value: 'view',
      label: 'Layout',
      icon: view === 'card' ? LayoutList : Table,
      show: showLayout,
      Component: LayoutToolbar,
      props: {
        page,
        view,
        toolbarOptionChange,
        label: 'Layout',
      },
    },
    {
      value: 'filter',
      label: 'Filter',
      icon: Funnel,
      show: true,
      props: {
        active: Boolean(filtersVisible),
        toolbarCount: filtersCount,
      },
    },
    {
      value: 'sort',
      label: 'Sort',
      icon: ArrowUpDown,
      show: view === 'table',
      Component: SortToolbar,
      props: {
        currentSort,
        toolbarOptionChange,
        label: 'Sort',
        sortOptions,
        selectedColumns,
        type,
      },
    },
    {
      value: 'fields',
      label: 'Fields',
      icon: Columns,
      Component: TableFields,
      show: view === 'table' && type !== 'public', // Hide fields selector for public views
      props: {
        selectedColumns,
        setSelectedColumns: (...args) => toolbarOptionChange('columns', ...args),
        tableRef,
        page,
      },
    },
    {
      value: 'group',
      label: 'Group',
      icon: TableCellsSplit,
      show: view === 'table' && false,
    },
  ].filter((option) => option.show);

  return (
    <Flex
      sx={{
        gap: '8px',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'relative',
        borderBottom: '1px solid',
        borderColor: 'border.subtle',
        pb: '8px',
        pr: '16px',
        pl: '8px',
        '& span': {
          display: ['none', 'none', 'none'],
        },
      }}
    >
      <Flex
        sx={{
          gap: '8px',
          flexWrap: 'wrap',
        }}
      >
        {options.map((option) => (
          <React.Fragment key={option.label}>
            {option.Component && <option.Component key={option.label} {...(option?.props ?? {})} />}
            {!option.Component && (
              <ToolbarButton option={option} toolbarOptionChange={toolbarOptionChange} {...(option?.props ?? {})} />
            )}
          </React.Fragment>
        ))}

        {otherToolbarOptions && otherToolbarOptions}
      </Flex>

      {type !== 'public' && (
        <Flex sx={{ width: '100px', justifyContent: isDownloadingCsv ? 'center' : 'flex-end' }}>
          {isDownloadingCsv && (
            <Flex sx={{ alignItems: 'center', justifyContent: 'center' }} width="24px" height="24px">
              <Loader size="small" />
            </Flex>
          )}
          {!isDownloadingCsv && (
            <ToolbarButton
              key="export"
              option={{
                value: 'export',
                label: 'Export',
                icon: Download,
              }}
              toolbarOptionChange={toolbarOptionChange}
              tooltip="Export a CSV of these filtered results to your inbox"
            />
          )}
        </Flex>
      )}
    </Flex>
  );
};

Toolbar.propTypes = {
  view: PropTypes.oneOf(['card', 'table']).isRequired,
  toolbarOptionChange: PropTypes.func.isRequired,
  selectedColumns: PropTypes.array.isRequired,
  otherToolbarOptions: PropTypes.node,
  isDownloadingCsv: PropTypes.bool,
  type: PropTypes.string,
  page: PropTypes.string.isRequired,
  filtersVisible: PropTypes.bool,
  filtersCount: PropTypes.number,
  tableRef: PropTypes.object.isRequired,
  currentSort: PropTypes.object,
  sortOptions: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string,
      label: PropTypes.string,
      value: PropTypes.string,
    }),
  ),
  showLayout: PropTypes.bool,
};

Toolbar.defaultProps = {
  otherToolbarOptions: null,
  isDownloadingCsv: false,
  type: 'default',
  filtersVisible: false,
  filtersCount: 0,
  currentSort: null,
  sortOptions: null,
  showLayout: true,
};
